import { racehorse360 } from "@tsg/1st-grpc-web-rh360";
import { HorseFilter } from "@tsg/1st-grpc-web-rh360/lib/stronachgroup/first/racehorse360/messages_pb";
import { SortOrder, SortOrderExtended } from "interfaces/SortOrder";
import { getStringMatchQueryField } from "utils/required-values-utils";

export const MIN_CHARS_NUMBER_FOR_SEARCH = 3;
export const MAX_SEARCH_RESULTS_COUNT = 99;

export type TSupportedEntity =
  | racehorse360.IHorse
  | racehorse360.ITrainer
  | racehorse360.IStallApplication;

export const getUniqueHorsesArrayFromEntities = (
  entities: (racehorse360.IWorkoutRequest | racehorse360.IUpcomingRaceEntry)[]
): racehorse360.IHorse[] => {
  if (!entities?.length) return [];

  return entities
    .map(wr => wr.horse)
    .reduce<racehorse360.IHorse[]>(
      (acc: racehorse360.IHorse[], cur: racehorse360.IHorse) =>
        acc.some(h => h.id === cur.id) ? acc : [...acc, cur],
      []
    );
};

export const createListHorsesRequest = (
  searchValue: string,
  facilityIds: string[],
  shouldSearchByChipNumber: boolean,
  maxResults: number = MAX_SEARCH_RESULTS_COUNT
): racehorse360.IListHorsesRequest => {
  const searchValueFilter: Partial<HorseFilter> = shouldSearchByChipNumber
    ? { chipNumber: getStringMatchQueryField("startsWith", searchValue) }
    : { name: getStringMatchQueryField("similarOrContains", searchValue) };
  const orderBy = shouldSearchByChipNumber
    ? [`name ${SortOrder.ASC}`]
    : [`name ${SortOrderExtended.ASC_SIMILARITY}`, `name ${SortOrder.ASC}`];

  return {
    query: {
      facilityIds,
      ...searchValueFilter
    },
    getOptions: {
      select: [
        "name",
        "trainer.firstName",
        "trainer.lastName",
        "gender",
        "birthday"
      ],
      orderBy
    },
    pagingOptions: {
      maxResults
    }
  };
};

export const createPullHorsesOrTrainersRequest = (
  searchValue: string,
  facilityIds: string[],
  maxResults: number = MAX_SEARCH_RESULTS_COUNT
): racehorse360.IPullHorsesOrTrainersRequest => {
  return {
    query: {
      facilityIds,
      horseOrTrainerName: getStringMatchQueryField("contains", searchValue)
    },
    pagingOptions: {
      maxResults
    }
  };
};

export const createListStallApplicationsRequest = (
  searchValue: string,
  filter: racehorse360.IStallApplicationFilter,
  maxResults: number = MAX_SEARCH_RESULTS_COUNT
): racehorse360.IListStallApplicationsRequest => {
  return {
    query: {
      ...filter,
      statuses: [
        racehorse360.StallApplicationStatus.STALL_APPLICATION_STATUS_APPLIED,
        racehorse360.StallApplicationStatus.STALL_APPLICATION_STATUS_COMPLETE,
        racehorse360.StallApplicationStatus
          .STALL_APPLICATION_STATUS_UNDER_REVIEW
      ],
      contactFullName: getStringMatchQueryField("contains", searchValue)
    },
    getOptions: {
      select: ["contactFullName", "status"]
    },
    pagingOptions: {
      maxResults
    }
  };
};

export const createListUpcomingRaceEntriesRequest = (
  searchValue: string,
  filter: racehorse360.IUpcomingRaceEntryFilter,
  shouldSearchByChipNumber: boolean,
  maxResults: number = MAX_SEARCH_RESULTS_COUNT
): racehorse360.IListUpcomingRaceEntriesRequest => {
  const searchValueFilter: Partial<racehorse360.IUpcomingRaceEntryFilter> =
    shouldSearchByChipNumber
      ? { horseChipNumber: getStringMatchQueryField("startsWith", searchValue) }
      : {
          horseOrTrainerName: getStringMatchQueryField(
            "similarOrContains",
            searchValue
          )
        };
  const orderBy = shouldSearchByChipNumber
    ? [`horse.name ${SortOrder.ASC}`]
    : [
        `horseOrTrainerName ${SortOrderExtended.ASC_SIMILARITY}`,
        `horse.name ${SortOrder.ASC}`
      ];

  return {
    query: {
      ...filter,
      ...searchValueFilter
    } as racehorse360.IUpcomingRaceEntryFilter,
    getOptions: {
      select: [
        "horse.name",
        "horse.trainer.firstName",
        "horse.trainer.lastName",
        "horse.gender",
        "horse.birthday"
      ],
      orderBy
    },
    pagingOptions: {
      maxResults
    }
  };
};

export const createListWorkoutRequestsRequest = (
  searchValue: string,
  filter: racehorse360.IWorkoutRequestFilter,
  shouldSearchByChipNumber: boolean,
  maxResults: number = MAX_SEARCH_RESULTS_COUNT
): racehorse360.IListWorkoutRequestsRequest => {
  const searchValueFilter: Partial<racehorse360.IWorkoutRequestFilter> =
    shouldSearchByChipNumber
      ? { horseChipNumber: getStringMatchQueryField("startsWith", searchValue) }
      : {
          horseOrTrainerName: getStringMatchQueryField(
            "similarOrContains",
            searchValue
          )
        };
  const orderBy = shouldSearchByChipNumber
    ? [`horse.name ${SortOrder.ASC}`]
    : [
        `horseOrTrainerName ${SortOrderExtended.ASC_SIMILARITY}`,
        `horse.name ${SortOrder.ASC}`
      ];

  return {
    query: {
      ...filter,
      ...searchValueFilter
    },
    getOptions: {
      select: [
        "horse.name",
        "horse.trainer.firstName",
        "horse.trainer.lastName",
        "horse.gender",
        "horse.birthday"
      ],
      orderBy
    },
    pagingOptions: {
      maxResults
    }
  };
};
